import { FC, useEffect, useCallback } from 'react';
import { Box, Toolbar, Container, Button, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { ChevronLeft } from '@mui/icons-material';
import { DeloitteStepper, DeloitteButton } from 'TaxAnalyticsUI/Deloitte';
import { Route, Switch, useHistory, useParams, useRouteMatch } from "react-router-dom";
import orderState, { serviceOfferState, currentOrderReference, currentServiceOfferName, serviceOfferStateDefault, entitiesState, currentEntityState, filesState, currentOrderMessagesState } from 'TaxAnalyticsState/orderState';
import { useRecoilState, useSetRecoilState, useResetRecoilState, useRecoilRefresher_UNSTABLE, useRecoilValue } from 'recoil';
import { ApiClient } from 'TaxAnalyticsHelpers';
import { createReferencePointers } from 'TaxAnalyticsHelpers/JSONDecycle';
import { snackbarState } from 'TaxAnalyticsState/portalState';
import { fetchUserOrders } from 'TaxAnalyticsState/userState';
import { flow } from 'TaxAnalyticsHelpers/Upload';
import { tag } from 'TaxAnalyticsHelpers/ATInternet';

interface OrderParams {
    name: string;
    step?: string;
    ref?: string;
}

export const Order: FC = (props: any) => {
    const history = useHistory();
    const { path } = useRouteMatch();
    const params = useParams<OrderParams>();
    const [order, setOrderState] = useRecoilState(orderState);
    const setFilesState = useSetRecoilState(filesState);
    const [currentEntity, setCurrentEntityState] = useRecoilState(currentEntityState);
    const setEntitiesState = useSetRecoilState(entitiesState);
    const setSnackbarState = useSetRecoilState(snackbarState);
    const [serviceOffer, setServiceOfferState] = useRecoilState(serviceOfferState);
    const setCurrentOrderRef = useSetRecoilState(currentOrderReference);
    const setCurrentServiceOfferNameRef = useSetRecoilState(currentServiceOfferName);
    const [orderMessages, setOrderMessage] = useRecoilState(currentOrderMessagesState);

    const resetOrder = useResetRecoilState(orderState);
    const resetServiceOfferState = useResetRecoilState(serviceOfferState);
    const resetCurrentOrderRef = useResetRecoilState(currentOrderReference);
    const resetCurrentServiceOfferNameRef = useResetRecoilState(currentServiceOfferName);
    const resetFileState = useResetRecoilState(filesState);

    const userOrders = useRecoilValue(fetchUserOrders);
    const refreshOrders = useRecoilRefresher_UNSTABLE(fetchUserOrders);

    const updateOrderPath = useCallback((offerName: string | null | undefined, step?: number | null, ref?: string, skipHistory?: boolean) => {
        const url = `/order/${offerName?.toUpperCase() ?? params.name.toUpperCase()}/${ref ?? params.ref}/${step ?? params.step}`
        const method = skipHistory ? history.replace : history.push;
        method(url);
    }, [params, history]);

    const hasNextStep = () => serviceOffer.currentStep + 1 < serviceOffer.steps.length;
    const hasPrevStep = () => serviceOffer.currentStep > 0;
    const nextStep = () => {
        setOrderMessage([]);
        if (hasNextStep()) {
            var newStep = serviceOffer.currentStep + 1;
            setServiceOfferState({ ...serviceOffer, currentStep: newStep })
            updateOrderPath(null, newStep, undefined, false);
            saveProgress();
        }
        else {
            if (order) {
                var updatedOrder = { ...order, status: 10, statusLabel: 'Commande envoyée' }
                ApiClient.serviceOffer.finalizeOrder(createReferencePointers(JSON.parse(JSON.stringify(updatedOrder))));
                setOrderState(updatedOrder);
            }
            setSnackbarState({
                message: `Votre demande d'analyse a été envoyée`,
                open: true,
                severity: 'success'
            })
            history.push("/workspace/dashboard");
        }

    }
    const prevStep = () => {
        if (hasPrevStep()) {
            var newStep = serviceOffer.currentStep - 1;
            setServiceOfferState({ ...serviceOffer, currentStep: newStep });
            setOrderMessage([]);
            updateOrderPath(null, newStep, undefined, false);
            saveProgress();
        }
    }

    const saveProgress = () => {
        if (order) {
            ApiClient.serviceOffer.updateOrder(createReferencePointers(JSON.parse(JSON.stringify(order))));
            refreshOrders();
        }
    }

    const containsErrors = (): boolean => {
        if (orderMessages)
            return orderMessages.filter(m => m.type === "error").length > 0;
        return false;
    }

    const fetchAll = async () => {
        if (params.name !== serviceOffer.offerName && (!params.ref || params.ref !== order?.tajReference)) {
            const newOrder = await ApiClient.serviceOffer.getByRef(params.ref, params.name);
            if (userOrders.findIndex(o => o.id === newOrder.id) === -1)
                refreshOrders();
            const entities = await ApiClient.entityClient.all();
            const orderEntity = entities.find(e => e.id === newOrder.entityId);
            if (orderEntity) {
                setCurrentEntityState(orderEntity);
                const files = await ApiClient.entityClient.orderFiles(newOrder.id);
                setFilesState(files);
            }
            setCurrentOrderRef(newOrder.tajReference ?? "");
            setCurrentServiceOfferNameRef(params.name ?? "")
            setOrderState(newOrder);
            setEntitiesState(entities);
            const step = parseInt(params.step ?? "0");
            const defaultOffer = serviceOfferStateDefault(params.name, step);
            setServiceOfferState({ ...defaultOffer, selectedLevel: (history.location.state as any)?.level });
            updateOrderPath(params.name, step, newOrder.tajReference, true);
            return;
        }
    }

    useEffect(() => {
        fetchAll();
        return () => {
            resetOrder();
            resetServiceOfferState();
            resetCurrentOrderRef();
            resetCurrentServiceOfferNameRef();
            resetFileState();
            flow.files = [];
        };
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (serviceOffer) {
            tag.sendPage({
                name: `Processus de commande: ${serviceOffer?.offer?.title} (étape:${serviceOffer.currentStep})`
            });
        }
    }, [serviceOffer]);

    return (
        <Box flexGrow={1} id="SubPage" sx={(theme) => {
            return {
                paddingBottom: theme.spacing(4),
                backgroundColor: theme.palette.background.paper
            }
        }}>
            <Toolbar sx={{
                backgroundColor: '#393C48',
                height: '80px'
            }}>
                <Container>
                    <Grid container alignItems="center">
                        <Grid xs={2}>
                            <Button startIcon={<ChevronLeft />} sx={{
                                color: '#FFFFFF',
                                textTransform: 'none',
                                lineHeight: '28px'
                            }} onClick={() => hasPrevStep() ? prevStep() : history.push("/workspace/dashboard")}>
                                Retour
                            </Button>
                        </Grid>
                        <Grid xs={8} sx={{
                            overflow: 'hidden'
                        }}>
                            <Typography align="center"> {serviceOffer?.offer?.title} - Commande : {order?.tajReference} {currentEntity ? ' - Entité: ' + currentEntity.name : ''}</Typography>
                        </Grid>
                        <Grid xs={2} />
                    </Grid>
                </Container>
            </Toolbar>
            <Container>
                <DeloitteStepper steps={serviceOffer.steps} currentStep={serviceOffer.currentStep} />
                <Switch>
                    {
                        serviceOffer.steps.map((step, index) =>
                            <Route key={index} exact path={`${path}/${step.step}`}>
                                {step.component}
                            </Route>)
                    }
                </Switch>
                <Grid container alignItems="center" justifyContent="center">
                    <Grid md={12} xs={12} container alignContent="center" justifyContent="space-evenly" alignItems="center">
                        <Grid>
                            {serviceOffer.currentStep !== 0 &&
                                <DeloitteButton variant="outlined" main onClick={() => prevStep()}>Précédent</DeloitteButton>
                            }
                        </Grid>
                        <Grid>
                            <DeloitteButton disabled={containsErrors()} variant="contained" color="secondary" main cta onClick={() => nextStep()}>{hasNextStep() ? "Suivant" : "Terminer"}</DeloitteButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}